/* eslint-disable react/prop-types */
/**
 * Pass G — /plan/physics-airframes catalogue (read-only).
 *
 * Fetches the FDM registry from ``GET /api/sim/physics-airframes``
 * once on mount, renders a sortable 30-row table with name, role,
 * mass and the ``AirframeAeroBadge`` decoration. Defence palette
 * only, tabular numerals on the mass column.
 *
 * Header is dynamic — N_total / N_calibrated / N_kinematic come
 * from the live response, not hard-coded.
 *
 * No edits, no actions — the catalogue is a procurement-review
 * surface so an evaluator can audit the threat-library coverage at
 * a glance without diving into the scenario editor.
 */
import { useEffect, useMemo, useState } from "react";

import { sim } from "@/api/endpoints";
import AirframeAeroBadge from "@/components/sim/AirframeAeroBadge";
import { DEFENCE_PALETTE } from "@/components/phase19/placementUtils";

const COLS = [
    { key: "name", label: "Name", numeric: false },
    { key: "role", label: "Role", numeric: false },
    { key: "mass_kg", label: "Mass (kg)", numeric: true },
    { key: "aero_calibrated", label: "Aero status", numeric: false },
];


export default function PhysicsAirframesCataloguePage() {
    const [rows, setRows] = useState(null);
    const [error, setError] = useState(null);
    const [sortKey, setSortKey] = useState("name");
    const [sortAsc, setSortAsc] = useState(true);

    useEffect(() => {
        let cancelled = false;
        sim.physicsAirframes.list().then(
            (data) => {
                if (cancelled) return;
                setRows(Array.isArray(data) ? data : []);
            },
            (err) => {
                if (cancelled) return;
                setError(err);
            },
        );
        return () => { cancelled = true; };
    }, []);

    const totals = useMemo(() => {
        if (!rows) return { total: 0, calibrated: 0, kinematic: 0 };
        const calibrated = rows.filter((r) => r.aero_calibrated).length;
        return {
            total: rows.length,
            calibrated,
            kinematic: rows.length - calibrated,
        };
    }, [rows]);

    const sorted = useMemo(() => {
        if (!rows) return [];
        const copy = [...rows];
        copy.sort((a, b) => {
            const av = a[sortKey];
            const bv = b[sortKey];
            if (av == null && bv == null) return 0;
            if (av == null) return 1;
            if (bv == null) return -1;
            if (typeof av === "number" && typeof bv === "number") {
                return sortAsc ? av - bv : bv - av;
            }
            if (typeof av === "boolean" && typeof bv === "boolean") {
                return sortAsc ? (av === bv ? 0 : av ? 1 : -1)
                    : (av === bv ? 0 : av ? -1 : 1);
            }
            return sortAsc
                ? String(av).localeCompare(String(bv))
                : String(bv).localeCompare(String(av));
        });
        return copy;
    }, [rows, sortKey, sortAsc]);

    function onHeaderClick(key) {
        if (key === sortKey) {
            setSortAsc((v) => !v);
        } else {
            setSortKey(key);
            setSortAsc(true);
        }
    }

    if (error) {
        return (
            <main
                className="mx-auto max-w-4xl px-6 py-10"
                data-testid="physics-airframes-catalogue-error"
            >
                <p style={{ color: DEFENCE_PALETTE.steelGray }}>
                    Could not load physics-airframe catalogue:{" "}
                    {String(error?.message || error)}
                </p>
            </main>
        );
    }

    return (
        <main
            className="mx-auto max-w-5xl px-6 py-10"
            style={{ color: DEFENCE_PALETTE.navy }}
            data-testid="physics-airframes-catalogue"
        >
            <header className="mb-6">
                <h1 className="text-2xl font-semibold tracking-tight">
                    Physics airframe catalogue
                </h1>
                <p
                    className="mt-2 text-sm"
                    style={{
                        color: DEFENCE_PALETTE.steelGray,
                        fontVariantNumeric: "tabular-nums",
                    }}
                    data-testid="physics-airframes-catalogue-header-totals"
                >
                    {totals.total} FDMs · {totals.calibrated} with 6-DoF
                    aero · {totals.kinematic} kinematic + mass
                </p>
            </header>

            <table
                className="w-full border-collapse text-sm"
                style={{ background: "#FFFFFF" }}
            >
                <thead>
                    <tr
                        style={{
                            borderBottom: `1px solid ${DEFENCE_PALETTE.steelGray}`,
                            background: "#F4F6F8",
                        }}
                    >
                        {COLS.map((c) => (
                            <th
                                key={c.key}
                                className={
                                    "cursor-pointer px-3 py-2 text-left "
                                    + "text-[11px] uppercase tracking-wide"
                                }
                                onClick={() => onHeaderClick(c.key)}
                                style={{ color: DEFENCE_PALETTE.steelGray }}
                                data-testid={`catalogue-col-${c.key}`}
                            >
                                {c.label}
                                {sortKey === c.key ? (
                                    <span aria-hidden="true">
                                        {sortAsc ? " ▲" : " ▼"}
                                    </span>
                                ) : null}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {sorted.map((r) => (
                        <tr
                            key={r.name}
                            style={{
                                borderBottom: `1px solid #E5E9ED`,
                            }}
                            data-testid={`catalogue-row-${r.name}`}
                        >
                            <td
                                className="px-3 py-2 font-medium"
                            >
                                {r.name}
                            </td>
                            <td
                                className="px-3 py-2"
                                style={{ color: DEFENCE_PALETTE.steelGray }}
                            >
                                {r.role}
                            </td>
                            <td
                                className="px-3 py-2 text-right"
                                style={{
                                    fontVariantNumeric: "tabular-nums",
                                }}
                            >
                                {r.mass_kg != null
                                    ? r.mass_kg.toFixed(2)
                                    : "—"}
                            </td>
                            <td className="px-3 py-2">
                                <AirframeAeroBadge
                                    aeroCalibrated={Boolean(r.aero_calibrated)}
                                />
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>

            <p
                className="mt-6 text-sm leading-relaxed"
                style={{ color: DEFENCE_PALETTE.steelGray }}
            >
                <strong>6-DoF aero</strong> — per-airframe aerodynamic
                coefficients calibrated; engagements run full 6-DoF
                JSBSim physics.{" "}
                <strong>Kinematic + mass</strong> — no per-airframe
                aerodynamic coefficients yet; engagements run kinematic
                stepper using FDM mass and drag coefficient. Per-
                airframe differentiation is via mass only.
            </p>
        </main>
    );
}
